<template>
  <div class="txs-app">
    <!-- Top bar -->
    <header class="txs-topbar">
      <button class="txs-icon-btn" @click="onBack" title="Back to dashboard">
        <i class="fa fa-arrow-left"></i>
      </button>
      <div class="txs-title">
        <h1>Tx Presenter Settings</h1>
        <div class="txs-sub" v-if="practiceName">{{ practiceName }}</div>
      </div>
      <div class="ml-auto">
        <span v-if="loading" class="txs-status">
          <i class="fa fa-circle-o-notch fa-spin"></i> Loading…
        </span>
      </div>
    </header>

    <div v-if="error" class="txs-error-banner">
      {{ error }}
      <button class="txs-icon-btn ml-auto" @click="clearError">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <!-- Body: sidebar + section content -->
    <div class="txs-body">
      <nav class="txs-sidebar">
        <div class="txs-sidebar-label">Sections</div>
        <button
          v-for="s in sections"
          :key="s.id"
          class="txs-nav-item"
          :class="{ active: activeSection === s.id, disabled: s.enabled === false }"
          :disabled="s.enabled === false"
          :title="s.enabled === false ? s.disabledReason : ''"
          @click="onSelectSection(s)"
        >
          <i class="fa" :class="'fa-' + s.icon"></i>
          <span>{{ s.label }}</span>
          <i v-if="s.enabled === false" class="fa fa-lock txs-nav-lock"></i>
        </button>
      </nav>

      <main class="txs-content tx-scroll">
        <SettingsGeneral
          v-if="activeSection === 'general'"
          :loading="loading"
          :saving="savingSection === 'general'"
          :practice-fallback-name="practiceName"
        />
        <SettingsPresentation
          v-else-if="activeSection === 'presentation'"
          :loading="loading"
          :saving="savingSection === 'presentation'"
        />
        <SettingsPaymentOptions
          v-else-if="activeSection === 'payment'"
          :loading="loading"
          :saving="savingSection === 'payment'"
        />
        <SettingsDelivery
          v-else-if="activeSection === 'delivery'"
          :loading="loading"
          :saving="savingSection === 'delivery'"
        />
        <SettingsConsent
          v-else-if="activeSection === 'consent'"
          :loading="loading"
          :saving="savingSection === 'consent'"
        />
        <div v-else class="txs-stub">
          <i class="fa fa-tools"></i>
          <p>This section is coming in a later slice.</p>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import mixin from '@/mixin.js';
import { TX_PRESENTER_SECTION_META } from './constants/settingsDefaults.js';
import SettingsGeneral from './components/settings/SettingsGeneral.vue';
import SettingsPresentation from './components/settings/SettingsPresentation.vue';
import SettingsPaymentOptions from './components/settings/SettingsPaymentOptions.vue';
import SettingsDelivery from './components/settings/SettingsDelivery.vue';
import SettingsConsent from './components/settings/SettingsConsent.vue';

export default {
  name: 'TxPresenterSettings',
  mixins: [mixin],
  components: { SettingsGeneral, SettingsPresentation, SettingsPaymentOptions, SettingsDelivery, SettingsConsent },

  data() {
    return {
      activeSection: 'general',
    };
  },

  computed: {
    ...mapState('txPresenter', { settingsState: (s) => s.settings }),
    ...mapGetters('txPresenter', ['formsPaidFor']),

    /**
     * Section nav, with per-site entitlement resolved onto the `enabled` flag that
     * TX_PRESENTER_SECTION_META has always carried but nothing read.
     *
     * Consent Forms is a Teamio Forms feature, so it needs FORMS_PAID_FOR. The tab
     * stays VISIBLE and greys out rather than disappearing — the practice should
     * still see the capability exists and how to get it.
     */
    sections() {
      return TX_PRESENTER_SECTION_META.map((s) => {
        if (s.id !== 'consent') return s;
        return {
          ...s,
          enabled: this.formsPaidFor,
          disabledReason: "Your office isn't enabled for Forms via Teamio. Please contact support to find out more information.",
        };
      });
    },
    loading() { return this.settingsState.loading; },
    error() { return this.settingsState.error; },
    savingSection() { return this.settingsState.savingSection; },

    site() {
      const sites = (this.$store.state.customer && this.$store.state.customer.sites) || [];
      return sites.find((s) => s.SiteID === this.siteID) || null;
    },
    practiceName() { return (this.site && this.site.Name) || ''; },
  },

  watch: {
    siteID: {
      immediate: true,
      handler(newSite) {
        if (newSite) this.bootstrap();
      },
    },
    '$route.query.section': {
      immediate: true,
      handler(s) {
        if (!s) return;
        // A deep link (?section=consent) bypasses the disabled nav button entirely,
        // so re-check entitlement here too. Falls back to General rather than
        // rendering a section the practice isn't entitled to.
        const target = String(s);
        const meta = this.sections.find((x) => x.id === target);
        if (meta && meta.enabled === false) {
          this.activeSection = 'general';
          return;
        }
        this.activeSection = target;
      },
    },
  },

  methods: {
    async bootstrap() {
      try {
        await this.$store.dispatch('txPresenter/loadSettings', { siteID: this.siteID });
      } catch (e) { /* in state */ }
    },
    onSelectSection(s) {
      if (s.enabled === false) return; // belt-and-braces; the button is also :disabled
      this.activeSection = s.id;
      this.$router.replace({ query: { ...this.$route.query, section: s.id } }).catch(() => {});
    },
    onBack() { this.$router.push({ name: 'TxPresenter' }); },
    clearError() {
      this.$store.commit('txPresenter/SET_SETTINGS_ERROR', null);
    },
  },
};
</script>

<style scoped>
.txs-app {
  --txs-bg: #f7f8fa;
  --txs-surface: #ffffff;
  --txs-surface2: #f1f3f6;
  --txs-border: #e4e7ec;
  --txs-text: #0f172a;
  --txs-text-mute: #475569;
  --txs-text-dim: #94a3b8;
  --txs-accent: #0d5f54;
  --txs-accent-soft: #e6f1ef;
  --txs-accent-ink: #094840;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  color: var(--txs-text);
  font-size: 14px;
  background: var(--txs-bg);
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
}
.txs-app * { box-sizing: border-box; }

.txs-topbar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 22px;
  background: var(--txs-surface);
  border-bottom: 1px solid var(--txs-border);
}
.txs-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--txs-border);
  background: var(--txs-surface);
  color: var(--txs-text-mute);
  border-radius: 8px;
  cursor: pointer;
}
.txs-icon-btn:hover { background: var(--txs-surface2); }
.txs-title h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.2px;
}
.txs-sub {
  font-size: 12px;
  color: var(--txs-text-dim);
  margin-top: 1px;
}
.txs-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--txs-text-dim);
}

.txs-error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
}

.txs-body {
  flex: 1;
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 0;
}

.txs-sidebar {
  background: var(--txs-surface);
  border-right: 1px solid var(--txs-border);
  padding: 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.txs-sidebar-label {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--txs-text-dim);
  font-weight: 600;
  padding: 4px 12px 10px;
}
.txs-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: var(--txs-text);
  text-align: left;
  border-radius: 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.1s, color 0.1s;
  white-space: nowrap;
}
.txs-nav-item .fa {
  width: 16px;
  text-align: center;
  color: var(--txs-text-mute);
  font-size: 13px;
}
.txs-nav-item:hover:not(.disabled) {
  background: var(--txs-surface2);
}
.txs-nav-item.active {
  background: var(--txs-accent-soft);
  color: var(--txs-accent-ink);
}
.txs-nav-item.active .fa { color: var(--txs-accent); }
.txs-nav-item.disabled {
  color: var(--txs-text-dim);
  cursor: not-allowed;
}
.txs-nav-item.disabled .fa { color: var(--txs-text-dim); opacity: 0.6; }
/* Lock badge on a section the practice isn't entitled to. Pushed right so the
   label stays aligned with the other sections — the tab is still visible on
   purpose, so it should read as "available, not yours yet", not as broken. */
.txs-nav-lock {
  margin-left: auto;
  font-size: 11px !important;
  opacity: 0.55;
}
.txs-nav-badge {
  margin-left: auto;
  font-size: 9.5px;
  padding: 1px 6px;
  background: var(--txs-surface2);
  border-radius: 999px;
  color: var(--txs-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
}

.txs-content {
  padding: 28px 32px;
  overflow-y: auto;
}
.txs-stub {
  text-align: center;
  padding: 60px 20px;
  color: var(--txs-text-dim);
  font-size: 13px;
}
.txs-stub .fa { font-size: 28px; margin-bottom: 10px; }

.ml-auto { margin-left: auto; }
</style>
